// One definition of an Optimizer run, shared by the Optimizer tab (Load → Run
// → Save) and the saved-portfolio Re-optimize action so the two cannot drift.
import { canonicalJsonObject } from './optimizerConstraintEditor';
import {
    MANUAL_OBJECTIVES, MAX_SHARPE_OBJECTIVE, designForManualRun, objectiveForDesign, tradeIdeaExpectedReturnPolicy,
    DESIGN_CONSTRAINT_SCHEMA, DESIGN_CONSTRAINT_DIGEST,
} from './optimizerObjectives';
import { META_OPTIMIZER_ENABLED } from './portfolioDesign';
import {
    OPTIMIZER_V5_CONSTRAINT_DIGEST,
    OPTIMIZER_V5_CONSTRAINT_SCHEMA,
    OPTIMIZER_V7_RECEIPT_CONTRACT,
    OPTIMIZER_V8_RECEIPT_CONTRACT,
    OPTIMIZER_V9_RECEIPT_CONTRACT,
    buildOptimizerV5ConstraintDocument,
    decodeOptimizerV2ConstraintDocument,
    optimizerV2VersionCommand,
} from './optimizerGenericGroupContract';

export const OPTIMIZER_RESTORATION_REQUIRED_MESSAGE = (
    'Restore the missing saved securities to the Watchlist before re-optimizing this portfolio.'
);
export const OPTIMIZER_OBJECTIVE_UNAVAILABLE_MESSAGE = (
    'Only Max Alpha and Max Sharpe are available for new optimization runs. '
    + 'Historical objectives remain available for viewing.'
);

export const optimizerViewModeForBaseline = (baselineSourceMode) => (
    baselineSourceMode === 'actual' ? 'normal' : 'pos-sizing'
);

export const optimizerBaselineForViewMode = (viewMode) => (
    viewMode === 'normal' ? 'actual' : 'pos_sizing'
);

export function getOptimizerFinancialInputError(constraints) {
    const financial = constraints?.static_constraints ?? {};
    const aum = Number(financial.aum);
    const gmv = Number(financial.gmv);
    if (!Number.isFinite(aum) || aum <= 0) {
        return 'A positive AUM is required before running the optimizer.';
    }
    if (!Number.isFinite(gmv) || gmv <= 0) {
        return 'A positive portfolio GMV is required before running the optimizer.';
    }
    return null;
}

export function getPosSizingOptimizerRunBlocker(context) {
    if (!context) {
        return 'Checking the saved Position Sizing context before this run.';
    }
    const digestIsValid = (
        typeof context.context_document_sha256 === 'string'
        && /^[0-9a-f]{64}$/.test(context.context_document_sha256)
    );
    const serverContextIsRunnable = (
        ['current', 'stale', 'unknown'].includes(context.freshness)
        && context.availability === 'available'
        && context.runnability === 'runnable'
        && digestIsValid
    );
    if (serverContextIsRunnable) {
        return null;
    }
    return context?.blocker?.message
        || 'The saved Position Sizing context is unavailable. Sync Draft before running the Optimizer.';
}

// Group limits whose category is no longer offered by the current Watchlist
// attribute catalog are dropped; without a catalog nothing is filtered yet.
export function filterGroupConstraintsToCatalog(groupConstraints, catalog) {
    if (!catalog) return groupConstraints;
    return groupConstraints.filter((record) => (
        catalog.definitions.some((definition) => (
            definition.attribute_definition_id === record.attribute_definition_id
            && definition.groups.some((group) => (
                group.group_kind === record.group_kind
                && group.group_value_key === record.group_value_key
            ))
        ))
    ));
}

// Saved portfolios and selected search results restore the same recorded limits.
export function optimizerReceiptRunConfiguration(
    detail,
    baselineSourceMode,
    { catalog = null } = {},
) {
    const decoded = decodeOptimizerV2ConstraintDocument(detail.constraint_document);
    const selectedCogniveIds = detail.securityRows
        .map(({ cognive_id: cogniveId }) => cogniveId)
        .sort();
    return {
        baselineSourceMode,
        viewMode: optimizerViewModeForBaseline(baselineSourceMode),
        selectedCogniveIds,
        constraints: { ...decoded.constraints, ...(detail.design_document ? { design: detail.design_document } : {}),
            ...(detail.expected_return_policy ? { expected_return_policy: detail.expected_return_policy } : {}) },
        constraintToggles: decoded.constraintToggles,
        groupConstraints: filterGroupConstraintsToCatalog(decoded.groupConstraints, catalog),
        objective: detail.design_document ? objectiveForDesign(detail.design_document) : detail.result.objective,
    };
}

// The saved selection and constraints: what Load installs in the Optimizer tab
// and what Re-optimize runs. `portfolio` is the saved-portfolio list item.
export function savedOptimizerRunConfiguration(detail, portfolio, options = {}) {
    return {
        ...optimizerReceiptRunConfiguration(detail, detail.portfolio.baseline_source_mode, options),
        loadedPortfolio: { portfolioId: portfolio.id, runOperationId: detail.receipt.run_operation_id, ...(detail.design_document ? { design: detail.design_document } : {}) },
    };
}

export function optimizerRunPreflightError({
    baselineSourceMode,
    selectedCogniveIds,
    constraints,
    constraintToggles,
    objective,
    capability,
    posSizingOptimizerContext,
}) {
    const maxSharpe = objective === MAX_SHARPE_OBJECTIVE;
    if (!maxSharpe && !(objective in MANUAL_OBJECTIVES) && objective !== 'PortfolioDesign') return OPTIMIZER_OBJECTIVE_UNAVAILABLE_MESSAGE;
    if (!maxSharpe && (objective !== 'AlphaScore' || constraints?.design) && !META_OPTIMIZER_ENABLED) return 'Portfolio construction objectives are not enabled in this environment.';
    if (maxSharpe) {
        // The total volatility limit is Max Sharpe's risk budget.
        const budget = constraints?.static_constraints?.port_vol_pct_aum;
        if (constraintToggles?.static_constraints?.port_vol_pct_aum !== true || !(Number.isFinite(budget) && budget > 0)) {
            return 'Max Sharpe needs a total volatility limit above 0%. Switch on Total volatility and set its limit.';
        }
        const skill = (constraints?.expected_return_policy ?? tradeIdeaExpectedReturnPolicy()).information_coefficient;
        if (!(Number.isFinite(skill) && skill > 0 && skill <= 1)) {
            return 'Enter a skill above 0 and at most 1 for Max Sharpe.';
        }
    }
    if (objective === 'PortfolioDesign' && !constraints?.design) return 'Reload this portfolio to restore its construction settings.';
    const scoreFloor = constraints?.design?.mandate?.score_floor;
    if (scoreFloor && (!Number.isFinite(scoreFloor.value)
        || (scoreFloor.mode === 'retention_pct' && (scoreFloor.value < 0 || scoreFloor.value > 1000)))) {
        return 'Enter a valid Alpha-Score floor. Percentage retention must be between 0% and 1000%.';
    }
    const financialInputError = getOptimizerFinancialInputError(constraints);
    if (financialInputError) return financialInputError;
    if (!selectedCogniveIds.length) {
        return 'Select at least one security before running the optimizer.';
    }
    if (baselineSourceMode === 'pos_sizing') {
        if (capability?.position_sizing_saved_context?.enabled !== true) {
            return 'Saved Position Sizing contexts are not available for '
                + 'generic Optimizer constraints yet.';
        }
        const blocker = getPosSizingOptimizerRunBlocker(posSizingOptimizerContext);
        if (blocker) return blocker;
    }
    if (
        constraintToggles?.static_constraints?.max_names_changed === true
        && capability?.cardinality?.enabled !== true
    ) {
        return 'Maximum names changed is not enabled in this environment.';
    }
    return null;
}

export function buildOptimizerRunCommand({
    baselineSourceMode,
    selectedCogniveIds,
    constraints,
    constraintToggles,
    groupConstraints,
    objective,
    posSizingContextDocumentSha256 = null,
    runOperationId,
}) {
    const design = designForManualRun(objective, constraints);
    const maxSharpe = objective === MAX_SHARPE_OBJECTIVE;
    return canonicalJsonObject({
        baseline_source_mode: baselineSourceMode,
        pos_sizing_context_document_sha256: baselineSourceMode === 'pos_sizing'
            ? posSizingContextDocumentSha256
            : null,
        pos_sizing_source_receipt: null,
        factor_momentum_parameters: null,
        canonical_cognive_ids: selectedCogniveIds,
        contract: maxSharpe ? OPTIMIZER_V9_RECEIPT_CONTRACT
            : design ? OPTIMIZER_V8_RECEIPT_CONTRACT : OPTIMIZER_V7_RECEIPT_CONTRACT,
        constraint_contract_digest: design ? DESIGN_CONSTRAINT_DIGEST : OPTIMIZER_V5_CONSTRAINT_DIGEST,
        constraint_schema_version: design ? DESIGN_CONSTRAINT_SCHEMA : OPTIMIZER_V5_CONSTRAINT_SCHEMA,
        constraints: buildOptimizerV5ConstraintDocument({
            constraints,
            constraintToggles,
            groupConstraints,
            selectedCogniveIds,
        }),
        factor_sharpes: null,
        objective: design ? 'PortfolioDesign' : objective,
        ...(design ? { design } : {}),
        ...(maxSharpe ? {
            expected_return_policy: constraints?.expected_return_policy ?? tradeIdeaExpectedReturnPolicy(),
        } : {}),
        run_operation_id: runOperationId,
    });
}

// `targetPortfolio` is the saved-portfolio list item to update, or null to
// create a new portfolio named `portfolioName`.
export function buildOptimizerSaveCommand({
    targetPortfolio = null,
    portfolioName = null,
    receiptId,
    saveOperationId,
}) {
    return optimizerV2VersionCommand({
        action: targetPortfolio ? 'update' : 'create',
        expected_portfolio_version: targetPortfolio ? targetPortfolio.portfolioVersion : 0,
        portfolio_name: targetPortfolio ? null : portfolioName,
        receipt_id: receiptId,
        save_operation_id: saveOperationId,
        target_portfolio_id: targetPortfolio ? targetPortfolio.id : null,
    });
}
